import type { PocInputValidity } from 'commons/types';
import { getUUID } from 'commons/utils/string.utils';
import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useState } from 'react';
import { RangeMode, ResultType } from 'modules/settings/pods/settings-materials/material-definition.vm';
import {
    DEFAULT_RESULT_TYPE,
    INITIAL_FORM_STATE,
    MAX_QC_LEVELS_PER_TEST,
    MaterialDefinitionFormState,
    QcLevelEditableField,
    QcLevelFormItem,
    rangeModeForResultType,
    TestConfigFormItem,
} from '../settings-material-definitions-form.model';
import {
    FormErrors,
    hasDataChanged,
    isFormDataValid,
    validateLotNumberMaxLength,
    validateQcTargetValues,
    validateRequiredFields,
} from '../settings-material-definitions-form.validation';

const VALIDITY_FIELDS: ReadonlyArray<keyof MaterialDefinitionFormState> = [
    'name',
    'longName',
    'provider',
    'type',
    'deviceTypeId',
    'testConfigs',
    'lotNumberMaxLength',
];

const RANGE_ERROR_KEYS: Partial<Record<keyof MaterialDefinitionFormState, string>> = {
    lotNumberMaxLength: 'settings.materials.material-definitions.form.errors.lot-number-max-length-range',
};

const seedLevels = (defaultLevelName: string): QcLevelFormItem[] => [{ name: defaultLevelName, clientKey: getUUID() }];

const clearOffModeDefaults = (level: QcLevelFormItem, rangeMode: RangeMode): QcLevelFormItem =>
    rangeMode === 'MIN_MAX'
        ? { ...level, defaultTarget: undefined, defaultDeviationPercent: undefined }
        : { ...level, defaultMin: undefined, defaultMax: undefined };

const levelsForRangeMode = (levels: QcLevelFormItem[] | undefined, rangeMode: RangeMode): QcLevelFormItem[] =>
    (levels ?? []).map((level) => clearOffModeDefaults(level, rangeMode));

const clearOffTypeDefaults = (level: QcLevelFormItem, resultType: ResultType): QcLevelFormItem =>
    resultType === 'QUANTITATIVE'
        ? { ...level, defaultTargetValues: undefined }
        : {
              ...level,
              defaultMin: undefined,
              defaultMax: undefined,
              defaultTarget: undefined,
              defaultDeviationPercent: undefined,
          };

const qcLevelsForResultType = (
    resultType: ResultType,
    existing: QcLevelFormItem[] | undefined,
    defaultLevelName: string
): QcLevelFormItem[] => {
    const levels = existing?.length ? existing : seedLevels(defaultLevelName);
    return levels.map((level) => clearOffTypeDefaults(level, resultType));
};

const newTestConfig = (defaultLevelName: string): TestConfigFormItem => ({
    clientKey: getUUID(),
    testId: '',
    resultType: DEFAULT_RESULT_TYPE,
    rangeMode: rangeModeForResultType(DEFAULT_RESULT_TYPE),
    qcLevels: seedLevels(defaultLevelName),
});

const parseOptionalNumber = (value: string | null): number | undefined => {
    if (value == null || value.trim() === '') {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const applyLevelField = (
    level: QcLevelFormItem,
    field: QcLevelEditableField,
    value: string | null
): QcLevelFormItem => {
    if (field === 'name') {
        return { ...level, name: value ?? '' };
    }
    if (field === 'defaultTargetValues') {
        return { ...level, defaultTargetValues: value ?? '' };
    }
    return { ...level, [field]: parseOptionalNumber(value) };
};

const isFormField = (field: string): field is keyof MaterialDefinitionFormState =>
    (VALIDITY_FIELDS as ReadonlyArray<string>).includes(field);

const getValidityErrorKey = (field: keyof MaterialDefinitionFormState, validity: PocInputValidity): string => {
    const errorTypes = (validity.type ?? []) as string[];
    if (errorTypes.includes('value-missing')) {
        return 'common.forms.errors.value-missing';
    }
    if (errorTypes.includes('too-long')) {
        return 'common.forms.errors.too-long';
    }
    if (errorTypes.includes('pattern-mismatch')) {
        return 'common.forms.errors.pattern-mismatch';
    }
    if (errorTypes.includes('range-underflow') || errorTypes.includes('range-overflow')) {
        return RANGE_ERROR_KEYS[field] ?? 'common.forms.errors.type-mismatch';
    }
    return 'common.forms.errors.type-mismatch';
};

interface UseMaterialDefinitionDataHandlerProps {
    onClose: VoidFunction;
    initialData?: MaterialDefinitionFormState | null;
    defaultLevelName: string;
}

export const useMaterialDefinitionDataHandler = ({
    onClose,
    initialData,
    defaultLevelName,
}: UseMaterialDefinitionDataHandlerProps) => {
    const baseline = initialData ?? INITIAL_FORM_STATE;
    const [formState, setFormState] = useState<MaterialDefinitionFormState>(baseline);
    const [errors, setErrors] = useState<FormErrors>({});
    const [initialDataSnapshot, setInitialDataSnapshot] = useState<MaterialDefinitionFormState>(baseline);
    const [observedInitialData, setObservedInitialData] = useState<MaterialDefinitionFormState | null | undefined>(
        initialData
    );
    const unsavedData = useCheckUnsavedChanges(onClose);

    // Sync state when `initialData` changes (edit-mode fetch resolves after first render).
    // Uses the render-time setState pattern from
    // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    if (initialData && initialData !== observedInitialData) {
        setObservedInitialData(initialData);
        setInitialDataSnapshot(initialData);
        setFormState(initialData);
        setErrors({});
        unsavedData.setIsUnsavedChanges(false);
    }

    const handleDataChange = (
        field: keyof MaterialDefinitionFormState,
        value: MaterialDefinitionFormState[keyof MaterialDefinitionFormState]
    ) => {
        setFormState((prev) => {
            const newState = { ...prev, [field]: value };
            unsavedData.setIsUnsavedChanges(hasDataChanged(newState, initialDataSnapshot));
            return newState;
        });
        setErrors((prev) => {
            const updated = { ...prev };
            if (field === 'lotNumberMaxLength' && value !== null) {
                const rangeError = validateLotNumberMaxLength(value as number);
                if (rangeError) {
                    updated.lotNumberMaxLength = rangeError;
                } else {
                    delete updated.lotNumberMaxLength;
                }
            } else {
                delete updated[field];
            }
            return updated;
        });
    };

    const handleValidity = (field: string, validity: PocInputValidity | null) => {
        if (!isFormField(field) || !validity) {
            return;
        }
        const errorTypes = (validity.type ?? []) as string[];
        if (validity.state === 'error' && errorTypes.length === 1 && errorTypes[0] === 'custom') {
            return;
        }
        setErrors((prev) => {
            const updated = { ...prev };
            if (validity.state === 'error') {
                updated[field] = getValidityErrorKey(field, validity);
            } else {
                delete updated[field];
            }
            return updated;
        });
    };

    const clearTestConfigsError = () =>
        setErrors((prev) => {
            const updated = { ...prev };
            delete updated.testConfigs;
            return updated;
        });

    const setTestConfigs = (nextOf: (prev: TestConfigFormItem[]) => TestConfigFormItem[]) => {
        setFormState((prev) => {
            const newState = { ...prev, testConfigs: nextOf(prev.testConfigs) };
            unsavedData.setIsUnsavedChanges(hasDataChanged(newState, initialDataSnapshot));
            return newState;
        });
    };

    const handleAddTest = () => {
        setTestConfigs((prev) => [...prev, newTestConfig(defaultLevelName)]);
        clearTestConfigsError();
    };

    const handleRemoveTest = (clientKey: string) => {
        setTestConfigs((prev) => prev.filter((testConfig) => testConfig.clientKey !== clientKey));
        clearTestConfigsError();
    };

    const updateTestConfig = (clientKey: string, updater: (testConfig: TestConfigFormItem) => TestConfigFormItem) =>
        setTestConfigs((prev) =>
            prev.map((testConfig) => (testConfig.clientKey === clientKey ? updater(testConfig) : testConfig))
        );

    const handleTestChange = (clientKey: string, testId: string) => {
        updateTestConfig(clientKey, (testConfig) => ({ ...testConfig, testId }));
        clearTestConfigsError();
    };

    const handleResultTypeChange = (clientKey: string, resultType: ResultType) =>
        updateTestConfig(clientKey, (testConfig) => ({
            ...testConfig,
            resultType,
            rangeMode: rangeModeForResultType(resultType, testConfig.rangeMode),
            qcLevels: qcLevelsForResultType(resultType, testConfig.qcLevels, defaultLevelName),
            possibleValues: resultType === 'QUANTITATIVE' ? undefined : testConfig.possibleValues,
        }));

    const handlePossibleValuesChange = (clientKey: string, possibleValues: string) =>
        updateTestConfig(clientKey, (testConfig) => ({ ...testConfig, possibleValues }));

    const handleRangeModeChange = (clientKey: string, rangeMode: RangeMode) =>
        updateTestConfig(clientKey, (testConfig) => ({
            ...testConfig,
            rangeMode,
            qcLevels: levelsForRangeMode(testConfig.qcLevels, rangeMode),
        }));

    const handleAddLevel = (clientKey: string, defaultName: string) =>
        updateTestConfig(clientKey, (testConfig) => {
            const levels = testConfig.qcLevels ?? [];
            if (levels.length >= MAX_QC_LEVELS_PER_TEST) {
                return testConfig;
            }
            return { ...testConfig, qcLevels: [...levels, { name: defaultName, clientKey: getUUID() }] };
        });

    const handleRemoveLevel = (clientKey: string, index: number) =>
        updateTestConfig(clientKey, (testConfig) => ({
            ...testConfig,
            qcLevels: (testConfig.qcLevels ?? []).filter((_, position) => position !== index),
        }));

    const handleLevelChange = (clientKey: string, index: number, field: QcLevelEditableField, value: string | null) =>
        updateTestConfig(clientKey, (testConfig) => ({
            ...testConfig,
            qcLevels: (testConfig.qcLevels ?? []).map((level, position) =>
                position === index ? applyLevelField(level, field, value) : level
            ),
        }));

    const validate = (): boolean => {
        const validationErrors = validateRequiredFields(formState);
        setErrors(validationErrors);
        // Target-values errors block Save without entering the section banner; they surface inline per row.
        const targetErrors = validateQcTargetValues(formState.testConfigs);
        return Object.keys(validationErrors).length === 0 && Object.keys(targetErrors).length === 0;
    };

    const isDataValid = (): boolean => hasDataChanged(formState, initialDataSnapshot) && isFormDataValid(formState);

    const resetForm = () => {
        setFormState(initialDataSnapshot);
        setErrors({});
        unsavedData.setIsUnsavedChanges(false);
    };

    return {
        formState,
        errors,
        handleDataChange,
        handleAddTest,
        handleRemoveTest,
        handleTestChange,
        handleResultTypeChange,
        handlePossibleValuesChange,
        handleRangeModeChange,
        handleAddLevel,
        handleRemoveLevel,
        handleLevelChange,
        handleValidity,
        validate,
        isDataValid,
        resetForm,
        unsavedData,
    };
};
